import React, { Fragment, ReactElement, ForwardedRef } from 'react';
import Checkbox from '@mui/material/Checkbox';
import TextBlock from '../text-block/text-block.component';
import CheckboxLabel from '../checkbox-label/checkbox-label.component';
import FormHelper from '../form-helper/form-helper.component';
import CheckboxIcon from '../../icons/checkbox-icon';
import CheckboxBlankIcon from '../../icons/checkbox-blank-icon';
import FormatService from '../../services/format-service';
import useStylesFieldCheckbox from './field-checkbox.styles';
import { DATA_REF_NAME } from '../../constants/system/data-attributes';

const FieldCheckbox: ReactMemoComponent = React.memo(React.forwardRef((
  props: ReactComponentProps,
  ref: ForwardedRef<HTMLDivElement>,
): ReactElement => {
  const ID_PREFIX_CHECKBOX_INPUT: string = 'checkbox-input';

  const {
    isPlain,
    isDefault,
    isDisabled,
    withNoOffset,
    withNoLabelOffset,
    withFormHelper,
    label,
    value,
    inputRefs,
    inputRefIndex,
    className,
    labelClassName,
    onChange,
    input,
    meta,
    ...custom
  } = props;

  const {
    touched: isTouched,
    error: formError,
    submitError: formSubmitError,
    modifiedSinceLastSubmit,
  } = (meta || {}) as FormFieldMetaProps;

  const { classes } = useStylesFieldCheckbox();

  const defaultClassName: string | undefined = isDefault ? classes.fieldCheckboxDefault : undefined;
  const noOffsetClassName: string | undefined = withNoOffset ? classes.fieldCheckboxNoOffset : undefined;
  const labelNoOffsetClassName: string | undefined = withNoLabelOffset
    ? classes.fieldCheckboxLabelNoOffset : undefined;

  const withErrors: boolean = isTouched && (!!formError || !!formSubmitError) && !modifiedSinceLastSubmit;
  const inputId: string | undefined = input && input.name ? `${ID_PREFIX_CHECKBOX_INPUT}-${input.name}` : undefined;

  return (
    <CheckboxLabel
      ref={ref}
      label={label || ''}
      className={FormatService.formatClassNames(labelNoOffsetClassName, labelClassName)}
    >
      <Fragment>
        <Checkbox
          disabled={isDisabled}
          icon={<CheckboxBlankIcon />}
          checkedIcon={<CheckboxIcon />}
          checked={isPlain ? value : !!input.value}
          error={(withErrors && formError) || null}
          id={inputId}
          className={FormatService.formatClassNames(
            classes.fieldCheckbox, noOffsetClassName, defaultClassName, className,
          )}
          inputRef={(
            inputRefs ? (ref: HTMLInputElement | null): void => {
              inputRefs.current[inputRefIndex] = ref;
            } : null
          )}
          inputProps={{
            [DATA_REF_NAME]: inputRefIndex,
          }}
          onChange={onChange}
          {...(!isPlain ? input : {})}
          {...custom}
        />
        {
          !isPlain && withFormHelper && withErrors && (
            <TextBlock className={classes.error}>
              <FormHelper isTouched={isTouched} formError={formError || formSubmitError} />
            </TextBlock>
          )
        }
      </Fragment>
    </CheckboxLabel>
  );
}));

export default FieldCheckbox;
